/**
 * @theme/morph — DOM diffing tuned for Shopify Section Rendering API responses.
 *
 * Goals:
 *   - Preserve form values, focus, scroll position, attached event listeners on retained nodes.
 *   - Preserve nested Component instances (custom elements) wherever possible — their state survives.
 *   - Honor `data-hydration-key` to opt regions out of diffing (replaced wholesale instead).
 *   - Honor `data-morph-skip` to leave a subtree completely untouched.
 *
 * NOT a general-purpose virtual DOM. This is good enough for Shopify section morphing and nothing more.
 */

const HYDRATION_KEY_ATTR = 'data-hydration-key';
const SKIP_ATTR = 'data-morph-skip';

/**
 * Morph the contents of `target` to match `source`. Both must be Elements.
 * Mutates `target` in place. Returns `target`.
 *
 * @param {Element} target
 * @param {Element} source
 * @returns {Element}
 */
export function morph(target, source) {
  if (target.hasAttribute(SKIP_ATTR)) return target;

  // If tag types or hydration keys differ, replace wholesale.
  if (
    target.tagName !== source.tagName ||
    target.getAttribute(HYDRATION_KEY_ATTR) !== source.getAttribute(HYDRATION_KEY_ATTR)
  ) {
    target.replaceWith(source.cloneNode(true));
    return /** @type {Element} */ (source);
  }

  syncAttributes(target, source);
  syncChildren(target, source);
  return target;
}

/**
 * Morph by section id. Looks up the existing element by id, parses the source HTML, and morphs.
 * The source HTML is expected to be the full section markup (Shopify Section Rendering API output).
 *
 * @param {string} sectionId — the Shopify section id (without the `shopify-section-` prefix is OK; we try both).
 * @param {string} html
 * @returns {Element | null} the morphed element, or null if not found.
 */
export function morphBySectionId(sectionId, html) {
  const candidates = [
    document.getElementById(`shopify-section-${sectionId}`),
    document.getElementById(sectionId),
  ].filter(Boolean);
  const target = /** @type {Element | null} */ (candidates[0]);
  if (!target) return null;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const sourceCandidates = [
    doc.getElementById(`shopify-section-${sectionId}`),
    doc.getElementById(sectionId),
    doc.body.firstElementChild,
  ].filter(Boolean);
  const source = /** @type {Element | null} */ (sourceCandidates[0]);
  if (!source) return null;
  return morph(target, source);
}

/* -------------------------------------------------------------------------- */
/* Internals                                                                  */
/* -------------------------------------------------------------------------- */

const PRESERVED_PROPERTIES = ['value', 'checked', 'selected'];

/**
 * @param {Element} target
 * @param {Element} source
 */
function syncAttributes(target, source) {
  // Add / update attributes.
  for (const attr of Array.from(source.attributes)) {
    if (target.getAttribute(attr.name) !== attr.value) {
      target.setAttribute(attr.name, attr.value);
    }
  }
  // Remove attributes that are no longer present.
  for (const attr of Array.from(target.attributes)) {
    if (!source.hasAttribute(attr.name)) {
      target.removeAttribute(attr.name);
    }
  }
  // Preserve form-control properties — they don't always reflect from attributes.
  if (target instanceof HTMLInputElement && source instanceof HTMLInputElement) {
    if (target.type !== 'file' && document.activeElement !== target) {
      // Keep focused input value untouched.
      target.value = source.value;
    }
    target.checked = source.checked;
  } else if (target instanceof HTMLTextAreaElement && source instanceof HTMLTextAreaElement) {
    if (document.activeElement !== target) target.value = source.value;
  } else if (target instanceof HTMLSelectElement && source instanceof HTMLSelectElement) {
    if (document.activeElement !== target) target.value = source.value;
  }
}

/**
 * @param {Element} target
 * @param {Element} source
 */
function syncChildren(target, source) {
  // If either side is marked skip, do nothing.
  if (target.hasAttribute(SKIP_ATTR)) return;

  // Build a quick lookup for keyed children on the target.
  /** @type {Map<string, Element>} */
  const keyedTargets = new Map();
  for (const child of Array.from(target.children)) {
    const key = child.getAttribute(HYDRATION_KEY_ATTR);
    if (key) keyedTargets.set(key, child);
  }

  /** @type {Element[]} */
  const sourceChildren = Array.from(source.children);
  /** @type {Node | null} */
  let cursor = target.firstChild;

  for (const sourceChild of sourceChildren) {
    const key = sourceChild.getAttribute(HYDRATION_KEY_ATTR);
    if (key && keyedTargets.has(key)) {
      const matched = keyedTargets.get(key);
      // Move into place if it isn't already there.
      if (matched !== cursor) {
        target.insertBefore(matched, cursor);
      }
      morph(matched, sourceChild);
      keyedTargets.delete(key);
      cursor = matched.nextSibling;
      continue;
    }

    // No keyed match. Try to reuse the cursor element if it's the same tag.
    if (cursor instanceof Element && cursor.tagName === sourceChild.tagName && !cursor.getAttribute(HYDRATION_KEY_ATTR)) {
      morph(cursor, sourceChild);
      cursor = cursor.nextSibling;
    } else {
      const insert = sourceChild.cloneNode(true);
      target.insertBefore(insert, cursor);
      // cursor stays put; we just inserted before it.
    }
  }

  // Sync text nodes (lightweight: we only handle pure text content if both sides are text).
  syncTextChildren(target, source);

  // Remove leftover children past the new length.
  while (cursor) {
    const next = cursor.nextSibling;
    target.removeChild(cursor);
    cursor = next;
  }
}

/**
 * Match text-only siblings: whitespace + text nodes. We don't try to be clever — we copy them.
 * @param {Element} target
 * @param {Element} source
 */
function syncTextChildren(target, source) {
  // Quick equality skip.
  if (target.childNodes.length !== source.childNodes.length) return;
  for (let i = 0; i < source.childNodes.length; i++) {
    const a = target.childNodes[i];
    const b = source.childNodes[i];
    if (a.nodeType === Node.TEXT_NODE && b.nodeType === Node.TEXT_NODE) {
      if (a.nodeValue !== b.nodeValue) a.nodeValue = b.nodeValue;
    }
  }
}
